import _ from 'lodash';
import BaseInputMixin from './BaseInputMixin';
import ClickOutside from './ClickOutside';

const VALID_TYPES = ['list', 'tags'];

export default {
  mixins: [
    BaseInputMixin,
    ClickOutside,
  ],

  props: {
    searchable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    caseSensitive: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    visibleSelections: {
      type: [Number, String],
      required: false,
      default: () => 1,
    },
    visibleItems: {
      type: [Number, String],
      required: false,
      default: () => 4,
    },
    listRelative: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    type: {
      type: String,
      required: false,
      default: () => 'list',
      validator: (value) => VALID_TYPES.includes(value),
    },
    animate: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    shadowItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    preventEmitOnMount: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },

  data() {
    return {
      search: '',
      tags: null,
      selectedListWidth: 0,
      listHeight: 0,
      listActive: false,
      filteredItems: [],
      internalItems: [],
    };
  },

  mounted() {
    if (!this.preventEmitOnMount && this.multiple && (!this.isValid(this.value, this.items) || !this.isArray(this.value))) {
      this.emitUpdate([]);
    }

    if (this.items) {
      this.itemList = this.items;
    }

    this.selectableItems = this.itemList;
  },

  updated() {
    setTimeout(() => {
      this.tags = this.$refs.selectedListItem;
      this.selectedListWidth = this.getSelectedListWidth(this.$refs.selectedListItem);
    }, 100);
  },

  watch: {
    items(val, prev) {
      this.itemList = val;
      if (val.length !== prev.length) {
        setTimeout(() => {
          this.listActive = false;
        }, 100);
      }
    },
    search(val) {
      if (`${val || ''}`.trim() === '') {
        this.selectableItems = this.itemList;

        return;
      }

      if (this.caseSensitive) {
        this.selectableItems = this.itemList.filter((x) => x.label.includes(val));
      } else {
        this.selectableItems = this.itemList.filter((x) => x.label.toLowerCase().includes(val.toLowerCase()));
      }
    },
    value() {
      this.search = '';
    },
    listActive(state) {
      this.$emit('listActive', state);
      if (state) {
        this.clickOutside('add', this.$refs.root, () => this.toggleList());
      } else {
        this.clickOutside('remove', this.$refs.root);
      }
    },
    itemList(val) {
      this.selectableItems = val.filter((x) => x.label == null || x.label.includes(this.search));
    },
    tags(tags) {
      if (!this.isArray(tags)) {
        return false;
      }
      setTimeout(() => {
        this.selectedListWidth = this.getSelectedListWidth(tags);
      }, 100);
      setTimeout(() => {
        this.scrollToLastTag();
      }, 200);
      return true;
    },
  },
  computed: {
    isList() {
      return this.getType === 'list';
    },
    isFileInput() {
      return this.classDefault.includes('file');
    },
    isSearchable() {
      return this.hasItems && this.searchable;
    },
    selectableItems: {
      get() {
        return this.filteredItems;
      },
      set(arr) {
        this.filteredItems = arr;
      },
    },
    hasItemSelected() {
      if (this.multiple) {
        return this.isValid(this.value, this.items) && (this.value instanceof Array) && !!this.value.length;
      } if (typeof this.value === 'number') {
        return this.isValid(this.value, this.items);
      } if (this.value instanceof Array) {
        return !!this.value.length;
      }
      return !!this.value;
    },
    itemList: {
      get() {
        return this.internalItems;
      },
      set(arr) {
        this.internalItems = arr;
      },
    },
    hasItems() {
      return this.shadowItems.length
        ? !!this.shadowItems.length
        : !!this.itemList.filter((x) => x.disabled !== true).length;
    },
    // Deprecated Features
    getListRelative() {
      return this.$attrs.relative || this.listRelative;
    },
    getType() {
      return this.$attrs.listType || this.type;
    },
  },
  methods: {
    toggleList() {
      if (this.disabled || this.listDisabled) {
        return false;
      }
      this.listActive = !this.listActive;
      return true;
    },
    selectItem(item, list) {
      this.search = '';
      if (this.isSelected(item, list)) {
        this.removeItem(item);
      } else {
        this.addItem(item);
      }
    },
    addItem(item) {
      if (this.isFileInput && this.multiple) {
        this.emitUpdate([...this.value, item.file]);
        this.validate(item.value);
      } else if (this.isFileInput && !this.multiple) {
        this.emitUpdate([item.file]);
      } else if (this.multiple) {
        this.emitUpdate([...this.value, item.value]);
        this.validate(item.value);
      } else {
        this.emitUpdate(item.value);
      }
    },
    removeItem(item) {
      if (this.isFileInput && this.multiple) {
        this.emitUpdate(this.value.filter((x) => x.name !== item.value));
        this.validate(item.value);
      } else if (this.isFileInput && !this.multiple) {
        this.emitUpdate([]);
      } else if (this.multiple) {
        this.emitUpdate(this.value.filter((x) => x !== item.value));
        this.validate(item.value);
      } else {
        this.emitUpdate(null);
      }
    },
    validate(val) {
      if (this.value.length > parseFloat(this.max)) {
        this.emitError('max', val);
      } else if (this.value.length < parseFloat(this.min)) {
        this.emitError('min', val);
      } else {
        this.emitSuccess(val);
      }
    },
    isArray(val) {
      return val instanceof Array;
    },
    isSelected(item, list = this.value) {
      if (this.multiple) {
        return list.length ? !!list.filter((x) => x === item.value).length : false;
      }
      return list === item.value;
    },
    getItem(val, list = this.itemList) {
      if (!this.hasItems) {
        return {
          label: val || '-',
        };
      }
      return this.shadowItems.length
        ? this.shadowItems.filter((x) => x.value === val)[0]
        : list.filter((x) => x.value === val)[0];
    },
    scrollToLastTag() {
      const { selectedList } = this.$refs;
      if (selectedList && this.multiple ? this.value.length > parseInt(this.visibleSelections, 10) : false) {
        selectedList.scroll({
          left: selectedList.scrollWidth,
          behavior: 'smooth',
        });
      }
    },
    getSelectedListWidth(tags) {
      if (tags instanceof Array) {
        const tagList = tags.map((x) => x.scrollWidth);
        const reversedTags = tagList.slice().reverse();

        if (tagList.length > parseInt(this.visibleSelections, 10)) {
          let result = 0;
          for (let i = 0; i < parseInt(this.visibleSelections, 10); i += 1) {
            const padding = i !== 0 && !this.isList ? 10 : 0;
            result += reversedTags[i] + padding;
          }

          return result;
        }
        const summedTags = reversedTags.reduce((pv, cv) => (!this.isList ? pv + cv + 10 : pv + cv), 0);

        return tagList.length ? summedTags : 0;
      }
      return tags ? tags.scrollWidth : 0;
    },
    getListHeight() {
      const padding = 0;
      if (this.selectableItems.length > parseInt(this.visibleItems, 10)) {
        let result = 0;
        for (let i = 0; i < parseInt(this.visibleItems, 10); i += 1) {
          result += this.itemHeight + padding;
        }

        return result;
      }
      return (this.itemHeight + padding) * this.selectableItems.length;
    },
    ClassesHook() {
      return {
        [`${this.classDefault}--open`]: this.listActive,
        [`${this.classDefault}--animate`]: (this.clearable && !this.hasValue && !this.isFileInput && this.animate) || (!this.clearable && !this.isFileInput && this.animate),
        [`${this.classDefault}--search`]: !!this.search,
        [`${this.classDefault}--multiple`]: this.multiple,
        [`${this.classDefault}--searchable`]: this.isSearchable,
        [`${this.classDefault}--${this.getType}`]: this.getType,
        'base-select--list-relative': this.getListRelative,
      };
    },
    ClearHook({ event }) {
      event.stopPropagation();
      this.search = '';
      this.listActive = false;
      this.toggleList();
    },
    EnterHook({ list }) {
      if (this.selectableItems.length === 1) {
        this.selectItem(this.selectableItems[0], list);
        this.toggleList();
      }
    },
    BackspaceHook({ list = this.value }) {
      // eslint-disable-next-line no-undef
      if (_.isEmpty(this.search)) {
        if (this.multiple) {
          const item = this.itemList.find((x) => x.value === list[list.length - 1]);

          if (typeof item !== 'undefined') this.selectItem(item, list);
        } else {
          this.emitUpdate(null);
        }
      }
    },
  },
};
